import React, { useState, useContext } from "react";
import {
  Button, Form, InputNumber, Tooltip, Switch,
} from "antd";
import { InfoCircleFilled } from "@ant-design/icons";
import { FormContext } from "./ScannerTableRow";
import ConfirmationModal from "../../../Common/ConfirmationModal";
import "./ScannerTableCell.css";

const ScannerTableCell = ({
  title,
  children,
  dataIndex,
  record,
  handleSubmit,
  handleRemoveMapping,
  updateSample,
  checkRowDisabled,
  checkDisableInteraction,
  validateInputs,
  submittingKey,
  removingKey,
  isResolvingHierarchy,
  isTntcTable,
  isTntcScannerMode,
  ...antdProps
}) => {
  const form = useContext(FormContext);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const modalBody = () => (
    <div>
      <div>
        Upon submitting the count,
        {" "}
        <strong>
          {record.analysis}
        </strong>
        {" "}
        will be automatically mapped to
        {" "}
        <strong>
          {record.petrifilm}
        </strong>
        .
      </div>
      <br />
      <div className="ScannerTable__ModalSubtext">
        <InfoCircleFilled className="ScannerTable__InfoIcon" />
        <div>
          If the mapping is incorrect or accidentally added, you can remove it by clicking on the
          {" "}
          <strong>&quot;Remove&quot;</strong>
          {" "}
          button in the previous pop up and submit again by scanning the correct petrifilm intended for this analysis.
        </div>
      </div>
    </div>
  );
  const save = async () => {
    try {
      const values = record.isTntcChecked && (isTntcTable || isTntcScannerMode) ? {} : await form.validateFields();
      setShowConfirmModal(false);
      handleSubmit(record, { ...values });
    } catch (errInfo) {
      // console.log("Save failed:", errInfo);
    }
  };

  const onTntcToggleChange = (checked, sample) => {
    // Clear the counts input
    if (checked) {
      form.setFieldValue("counts", "");
    } else if (sample.counts) {
      // reset to its original counts value if it can be parsed to an integer
      form.setFieldValue("counts", Number.isNaN(Number(sample.counts)) ? "" : Number(sample.counts));
    }
    updateSample(record.key, { ...sample, isTntcChecked: checked });
  };

  const handleUnmappedConfitmation = async () => {
    const values = record.isTntcChecked && (isTntcTable || isTntcScannerMode) ? {} : await form.validateFields();
    if (validateInputs(record, { ...values })) {
      setShowConfirmModal(true);
    }
  };

  let childNode = children;

  /** Petrifilm: hide petrifilm value if mapping is false */
  if (dataIndex === "petrifilm") {
    childNode = <span>{record.mapping ? record.petrifilm : ""}</span>;
  }

  if (dataIndex === "tntc") {
    const allowSwitch = ((record.status === "Ready" || record.status === "TNTC" || isResolvingHierarchy) && !record.block && record.edit) || record.status === "Pending";
    childNode = <Switch disabled={!allowSwitch} checked={record.isTntcChecked} onChange={(checked) => onTntcToggleChange(checked, record)} />;
  }

  /** Counts: allow user to input value */
  if (dataIndex === "counts") {
    const disabled = (dataIndex === "counts" && checkRowDisabled(record)) || record.block;
    const disableCountValue = "-";
    const rawCountValue = record[dataIndex];
    const isTntcCountToken = rawCountValue === "TNTC" || rawCountValue === "tntc";
    const initialValue = rawCountValue !== undefined && !isTntcCountToken
      ? parseInt(rawCountValue, 10)
      : undefined;
    childNode = disabled ? <span>{record[dataIndex] ?? disableCountValue}</span> : (
      <Form.Item
        className="ScannerTableCell__FormItem"
        name={dataIndex}
        initialValue={initialValue}
        rules={[
          {
            required: true,
            message: "",
          },
          {
            type: "number",
            message: `${title} must be a number`,
          },
        ]}
      >
        <InputNumber
          className={`ScannerTable__Input${checkDisableInteraction() ? "--disabled" : ""}`}
          disabled={checkDisableInteraction() || (record.isTntcChecked && (isTntcTable || isTntcScannerMode))}
          placeholder="Enter raw count"
          min={0}
        />
      </Form.Item>
    );
  }

  const tooltipTitle = () => {
    /** When mapping is ture */
    if (record.mapping) {
      /** If there is count value */
      if (record.counts) {
        return (
          <span>
            This will
            <br />
            1. Reset the Raw Count.
            <br />
            2. Remove the petrifilm mapping.
          </span>
        );
      }
      /** If there is no count value */
      return (
        <span>
          This will remove the petrifilm mapping.
        </span>
      );
    }
    /** When mapping is false */
    return (<span>Current analysis does not have a mapping.</span>);
  };

  /** Action: show submit button */
  if (dataIndex === "action") {
    const submitText = isResolvingHierarchy ? "Save" : "Submit";
    childNode = (
      <div className="ScannerTable__ActionCell">
        {checkRowDisabled(record) ? (
          <span>
            { (record.status === "Ready" || isResolvingHierarchy || record.status === "TNTC") && !record.block ? (
              <Button
                onClick={() => updateSample(record.key, { ...record, edit: true })}
                disabled={checkDisableInteraction()}
              >
                Edit
              </Button>
            ) : "Submitted" }
          </span>
        )
          : (
            <Button
              onClick={record.mapping ? save : handleUnmappedConfitmation}
              loading={submittingKey === record.key}
              disabled={checkDisableInteraction()}
            >
              {record.recheck ? "Verify" : submitText}
            </Button>
          )}
        <ConfirmationModal
          title="Analysis Mapping"
          open={showConfirmModal}
          bodyComponent={modalBody}
          okText="Submit"
          cancelText="Cancel"
          width="500px"
          onCancel={() => setShowConfirmModal(false)}
          onOk={save}
        />
      </div>
    );
  }

  /** Mapping: show the Remove button */
  if (dataIndex === "mapping") {
    childNode = (
      <div className="ScannerTable__ActionCell">
        <Tooltip
          title={tooltipTitle()}
        >
          <Button
            onClick={() => handleRemoveMapping(record, form)}
            loading={removingKey === record.key}
            disabled={!record.mapping || checkDisableInteraction()}
          >
            Remove
          </Button>
        </Tooltip>
      </div>
    );
  }

  /** All other columns: show string value */
  return <td {...antdProps}>{childNode}</td>; // eslint-disable-line react/jsx-props-no-spreading
};

export default ScannerTableCell;
